import React, { useState } from 'react';
import { XMarkIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import type { RecordPaymentDto, PaymentMethod } from '../types/billing.types';

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  check: 'Check',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  wire_transfer: 'Wire Transfer',
  ach: 'ACH / eCheck',
  cash: 'Cash',
  online_portal: 'Online Portal',
  other: 'Other',
};

interface RecordPaymentModalProps {
  open: boolean;
  billId: string;
  billNumber: string;
  balanceDue: number;
  currency?: string;
  enableTrustAccounting?: boolean;
  onRecord: (dto: RecordPaymentDto) => Promise<void>;
  onClose: () => void;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  open,
  billId,
  billNumber,
  balanceDue,
  currency = 'USD',
  enableTrustAccounting = false,
  onRecord,
  onClose,
}) => {
  // Form state
  const [amount, setAmount] = useState(balanceDue > 0 ? balanceDue.toFixed(2) : '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('check');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isTrustPayment, setIsTrustPayment] = useState(false);

  // Check-specific fields
  const [checkNumber, setCheckNumber] = useState('');
  const [bankName, setBankName] = useState('');

  // Card-specific fields
  const [cardLastFour, setCardLastFour] = useState('');
  const [cardType, setCardType] = useState('');

  // Payer info
  const [payerName, setPayerName] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const parsedAmount = Number(amount) || 0;
  const isOverpayment = parsedAmount > balanceDue && balanceDue > 0;

  const handleSubmit = async () => {
    if (parsedAmount <= 0) {
      setError('Payment amount must be greater than 0.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const dto: RecordPaymentDto = {
        bill_id: billId,
        amount: parsedAmount,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        reference_number: referenceNumber.trim() || undefined,
        notes: notes.trim() || undefined,
        is_trust_payment: isTrustPayment || undefined,
        payer_name: payerName.trim() || undefined,
        check_number: checkNumber.trim() || undefined,
        bank_name: bankName.trim() || undefined,
        card_last_four: cardLastFour.trim() || undefined,
        card_type: cardType.trim() || undefined,
      };
      await onRecord(dto);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  const showCheckFields = paymentMethod === 'check';
  const showCardFields = paymentMethod === 'credit_card' || paymentMethod === 'debit_card';

  const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleCancel}>
      <div className="absolute inset-0 bg-black/50" />

      <div
        className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
              <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Record Payment
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {billNumber} &middot; Balance due: ${balanceDue.toFixed(2)} {currency}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Amount + Payment Method row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={`${inputClass} pl-7`}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              {isOverpayment && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Overpayment of ${(parsedAmount - balanceDue).toFixed(2)} will be tracked as unapplied.
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>Payment Method *</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                className={inputClass}
              >
                {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Payment Date + Reference Number row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Payment Date *</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Reference / Confirmation #</label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className={inputClass}
                placeholder={showCheckFields ? 'Check number' : 'Confirmation #'}
              />
            </div>
          </div>

          {/* Check-specific fields */}
          {showCheckFields && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Check Number</label>
                <input
                  type="text"
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                  className={inputClass}
                  placeholder="1234"
                />
              </div>
              <div>
                <label className={labelClass}>Bank Name</label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className={inputClass}
                  placeholder="Chase, Wells Fargo..."
                />
              </div>
            </div>
          )}

          {/* Card-specific fields */}
          {showCardFields && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Last 4 Digits</label>
                <input
                  type="text"
                  maxLength={4}
                  value={cardLastFour}
                  onChange={(e) => setCardLastFour(e.target.value.replace(/\D/g, ''))}
                  className={inputClass}
                  placeholder="4242"
                />
              </div>
              <div>
                <label className={labelClass}>Card Type</label>
                <select
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  <option value="Visa">Visa</option>
                  <option value="MasterCard">MasterCard</option>
                  <option value="Amex">Amex</option>
                  <option value="Discover">Discover</option>
                </select>
              </div>
            </div>
          )}

          {/* Payer Name */}
          <div>
            <label className={labelClass}>Payer Name</label>
            <input
              type="text"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              className={inputClass}
              placeholder="Name on check or card (if different from client)"
            />
          </div>

          {/* Trust Payment Toggle */}
          {enableTrustAccounting && (
            <div className="flex items-center gap-3 p-3 bg-lex-accent-soft rounded-lg border border-lex-accent">
              <input
                type="checkbox"
                id="trustPayment"
                checked={isTrustPayment}
                onChange={(e) => setIsTrustPayment(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <label htmlFor="trustPayment" className="text-sm text-lex-accent">
                Pay from client trust account
                <span className="block text-xs text-lex-accent">
                  Funds will be transferred from trust to operating account
                </span>
              </label>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${inputClass} resize-none`}
              rows={2}
              placeholder="Internal payment notes..."
            />
          </div>

          {/* Error display */}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {parsedAmount > 0 && (
              <span>
                Recording <span className="font-semibold text-gray-900 dark:text-white">${parsedAmount.toFixed(2)}</span>
                {parsedAmount >= balanceDue && balanceDue > 0 && (
                  <span className="ml-1 text-green-600 dark:text-green-400">(full payment)</span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || parsedAmount <= 0}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <CurrencyDollarIcon className="h-4 w-4" />
              {submitting ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
